import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, AlertTriangle, Sparkles, Brain } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import type { TradeVerticalId } from './verticalIds';
import { TRADE_VERTICAL_LABELS } from './verticalIds';

type PnlV = { realized_today?: number; unrealized?: number; open_count?: number } | undefined;

type Props = {
  verticalId: string;
  goals: Record<string, unknown>;
  pnl: PnlV;
};

/**
 * Sprint 5 — goals vs realized + quick links to improve (pattern suggestions, intelligence).
 */
export default function VerticalGrowthStrip({ verticalId, goals, pnl }: Props) {
  const label = TRADE_VERTICAL_LABELS[verticalId as TradeVerticalId] || verticalId;
  const cap = goals.max_daily_loss_usd;
  const capNum = cap != null && cap !== '' ? Number(cap) : null;
  const realized = pnl ? Number(pnl.realized_today ?? 0) : 0;
  const warnCap = capNum != null && Number.isFinite(capNum) && realized <= -Math.abs(capNum);

  return (
    <Card className="border-primary/20 bg-primary/[0.03]">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Growth — {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm space-y-3">
        <div className="flex flex-wrap gap-4 text-muted-foreground">
          <div>
            <span className="text-xs uppercase tracking-wide block">Realized today (UTC)</span>
            <span className="font-mono text-lg text-foreground font-semibold">{realized.toFixed(2)} USD</span>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wide block">Max daily loss cap</span>
            <span className="font-mono text-lg text-foreground font-semibold">
              {capNum != null && Number.isFinite(capNum) ? `${capNum} USD` : '—'}
            </span>
          </div>
          {pnl ? (
            <div>
              <span className="text-xs uppercase tracking-wide block">Unrealized</span>
              <span className="font-mono text-lg text-foreground font-semibold">
                {Number(pnl.unrealized ?? 0).toFixed(2)} USD
              </span>
            </div>
          ) : null}
        </div>
        {warnCap ? (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-100">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              {"Today's realized PnL is beyond the configured daily loss cap directionally — review gates, patterns,"}
              and risk on the Overview and Decisions tabs.
            </span>
          </div>
        ) : null}
        <div className="flex flex-wrap gap-3 text-xs">
          <Link
            to={`/discovery/patterns?vertical=${verticalId}`}
            className="inline-flex items-center gap-1 text-primary font-medium underline-offset-4 hover:underline"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Pattern suggestions
          </Link>
          <Link to="/intelligence" className="inline-flex items-center gap-1 text-primary font-medium underline-offset-4 hover:underline">
            <Brain className="w-3.5 h-3.5" />
            Intelligence & gates
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
